import { flexRender, Header, SortingState } from '@tanstack/react-table';
import {
  DropdownEntry,
  DropdownMenu,
  DropdownRadioGroup,
  Icon,
  PinConfig,
} from '@toggle/design-system';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { INSIGHTS_COL_ID } from '../get-columns';
import { InsightsHeaderCell } from '../insights-header-cell/InsightsHeaderCell';
import { EntityPlusPrice } from '../useAssetsTableData';
import { useAssetsTableStore } from '../useAssetsTableStore';
import * as S from './AssetsTableHeaderCell.styles';

export interface AssetsTableHeaderCellProps {
  header: Header<EntityPlusPrice, unknown>;
  pinConfig?: PinConfig;
}

export const AssetsTableHeaderCell: FC<AssetsTableHeaderCellProps> = ({
  header,
  pinConfig,
}) => {
  const { t } = useTranslation('widget');
  const sortOrder = header.column.getIsSorted();

  const setSortingState = (sortingState: SortingState) => {
    useAssetsTableStore.setState({ sortingState });
  };

  const sortOptions = [
    {
      label: t('common:table:dropdown', { context: 'ascending' }),
      iconName: 'SortUp',
      key: 'asc',
      onClick: () =>
        setSortingState([
          {
            id: header.column.id,
            desc: false,
          },
        ]),
    },
    {
      label: t('common:table:dropdown', { context: 'descending' }),
      iconName: 'SortDown',
      key: 'desc',
      onClick: () =>
        setSortingState([
          {
            id: header.column.id,
            desc: true,
          },
        ]),
    },
    sortOrder && {
      label: t('common:table:dropdown', { context: 'clearSort' }),
      iconName: 'ClearSort',
      key: 'clear_sort',
      onClick: () => {
        setSortingState([]);
      },
    },
  ].filter(Boolean) as DropdownEntry[];

  const getCellAlignment = (header: Header<EntityPlusPrice, unknown>) => {
    if (header.column.id === 'insights1W') {
      return 'left';
    }
    return header.column.getIsPinned() ? 'left' : 'right';
  };

  const tableHeader = (
    <S.StyledStickyTableHeader
      alignment={getCellAlignment(header)}
      key={header.id}
      pinConfig={pinConfig}
      size={header.column.getSize()}
      $isClickable={
        header.column.getCanSort() || header.column.id === INSIGHTS_COL_ID
      }
      $isSorted={!!header.column.getIsSorted()}
    >
      {header.column.getCanSort() && sortOrder && (
        <Icon
          iconName={sortOrder === 'asc' ? 'SortAscRight' : 'SortDescRight'}
          size={12}
        />
      )}
      {flexRender(header.column.columnDef.header, header.getContext())}
    </S.StyledStickyTableHeader>
  );

  if (header.column.id === INSIGHTS_COL_ID) {
    return <InsightsHeaderCell header={tableHeader} />;
  }

  if (header.column.getCanSort()) {
    const selectedKey = sortOrder || undefined;
    return (
      <DropdownMenu key={header.id} triggerNode={tableHeader}>
        <DropdownRadioGroup items={sortOptions} selectedKey={selectedKey} />
      </DropdownMenu>
    );
  }

  return tableHeader;
};
